// Detects unsupported legacy control-flow closers like `/for}`.
// Compiler now requires standard `}` closures for control blocks.
// Throw a compile error when legacy control closer syntax is detected.
export function assertNoLegacyControlClosers(source: string): void {
  let index = 0
  let quote: "'" | '"' | null = null
  let inLineComment = false
  let inBlockComment = false
  let line = 1

  // Scan source and reject legacy closer tokens outside comments/quotes.
  while (index < source.length) {
    const char = source[index]
    const next = source[index + 1] ?? ''
    const previous = index > 0 ? source[index - 1] : ''

    // Keep line comments opaque while scanning for legacy closers.
    if (inLineComment) {
      if (char === '\n') {
        inLineComment = false
        line += 1
      } else {
        // Continue line-comment scan.
      }
      index += 1
      continue
    } else if (inBlockComment) {
      // Keep block comments opaque while scanning for legacy closers.
      if (char === '*' && next === '/') {
        inBlockComment = false
        index += 2
        continue
      } else if (char === '\n') {
        // Track line number inside block comments.
        line += 1
      } else {
        // Continue block-comment scan.
      }
      index += 1
      continue
    } else {
      // Continue scanning outside comments.
    }

    // Keep quoted segments opaque to legacy-closer detection.
    if (quote) {
      if (char === quote && previous !== '\\') {
        quote = null
      } else if (char === '\n') {
        // Track line number inside quoted text.
        line += 1
      } else {
        // Continue quoted-segment scan.
      }
      index += 1
      continue
    } else {
      // Continue scanner outside quoted segments.
    }

    // Enter comment modes when scanner sees comment delimiters.
    if (char === '/' && next === '*') {
      inBlockComment = true
      index += 2
      continue
    } else if (char === '/' && next === '/') {
      inLineComment = true
      index += 2
      continue
    } else {
      // Continue scanning non-comment tokens.
    }

    // Enter quote mode on `'` or `"` delimiters.
    if (char === "'" || char === '"') {
      quote = char
      index += 1
      continue
    } else {
      // Continue legacy closer detection.
    }

    const closer = parseLegacyControlCloser(source, index)
    // Throw explicit syntax error for unsupported legacy closers.
    if (closer) {
      throw new Error(
        `Invalid .luis syntax: legacy control closer "/${closer.keyword}}" is not supported at line ${line}. Use "}" instead.`,
      )
    } else if (char === '\n') {
      // Track line numbers in regular source scan.
      line += 1
    } else {
      // Keep line unchanged for non-newline chars.
    }

    index += 1
  }
}
// Parse one legacy closer token candidate at scanner cursor.
function parseLegacyControlCloser(source: string, startIndex: number): { keyword: string } | null {
  // Reject non-legacy-closer positions quickly.
  if (source[startIndex] !== '/') {
    return null
  } else {
    // Continue legacy closer parsing.
  }

  let cursor = startIndex + 1
  const keywordStart = cursor
  // Consume alphabetical keyword chars.
  while (cursor < source.length && isAlpha(source[cursor])) {
    cursor += 1
  }

  const keyword = source.slice(keywordStart, cursor).toLowerCase()
  // Reject unknown legacy closer keywords.
  if (!isLegacyCloserKeyword(keyword)) {
    return null
  } else {
    // Continue suffix parsing for closing brace.
  }

  // Skip spaces before the required trailing `}`.
  while (cursor < source.length && isWhitespace(source[cursor])) {
    cursor += 1
  }

  // Reject tokens without required trailing `}`.
  if (source[cursor] !== '}') {
    return null
  } else {
    // Return parsed legacy closer keyword payload.
    return { keyword }
  }
}

// Check whether one keyword is a legacy control closer type.
function isLegacyCloserKeyword(keyword: string): boolean {
  return keyword === 'for' || keyword === 'if' || keyword === 'elseif' || keyword === 'else'
}

// Check whether one character is alphabetical.
function isAlpha(char: string): boolean {
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')
}

// Check whether one character is whitespace.
function isWhitespace(char: string): boolean {
  return char === ' ' || char === '\t' || char === '\n' || char === '\r' || char === '\f'
}
